/**
 * Application root: fetches runtime config from the backend, initialises MSAL
 * when auth is enabled, then renders the app.
 *
 * Split out of main.tsx so the entry file contains no component — React Fast
 * Refresh cannot replace a module that exports nothing.
 *
 * With auth switched off (local development) `AuthProvider` still calls
 * `useMsal()`, which needs a provider — so an inert client is created once for
 * it. It used to be built inline in the JSX, i.e. on every render.
 *
 * Inside the Media Manager's frame, when the API accepts its sign-in
 * (`media_manager_sign_in`), the app runs on that sign-in
 * (`contexts/HostAuthContext.tsx`) and starts no MSAL of its own.
 */

import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { StrictMode, useEffect, useState } from 'react'

import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary'
import StartScreen, { StartError, StartProgress } from './components/StartScreen'
import { createMsalConfig, type RuntimeConfig } from './config/authConfig'
import { AuthProvider } from './contexts/AuthContext'
import { HostAuthProvider } from './contexts/HostAuthContext'
import { host } from './embed'

function usesHostSignIn(config: RuntimeConfig): boolean {
    return host !== null && config.auth_enabled && config.media_manager_sign_in === true
}

/**
 * Whether this window is a sign-in popup MSAL opened (`reauthenticate`): the
 * opener reads the answer from its address and closes it. The app must not
 * start here — its hash routes would overwrite `#code=…` before the opener
 * reads it.
 */
function isMsalPopup(): boolean {
    return !!window.opener && window.opener !== window && window.name.startsWith('msal.')
}

async function fetchWithRetry(url: string, retries = 3, delay = 1000): Promise<Response> {
    for (let attempt = 0; attempt < retries; attempt++) {
        try {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 5000)
            const res = await fetch(url, { signal: controller.signal })
            clearTimeout(timeoutId)
            if (res.ok) return res
        } catch (err) {
            if (attempt === retries - 1) throw err
        }
        await new Promise((resolve) => setTimeout(resolve, delay * (attempt + 1)))
    }
    throw new Error('Failed after retries')
}

/** Why the app could not start: a German title and text, and MSAL's own words for support. */
interface StartFailure {
    title: string
    message: string
    detail?: string
}

export const Root = () => {
    const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null)
    const [config, setConfig] = useState<RuntimeConfig | null>(null)
    const [failure, setFailure] = useState<StartFailure | null>(null)

    useEffect(() => {
        if (isMsalPopup()) return
        const init = async () => {
            try {
                const res = await fetchWithRetry('/api/config')
                const runtimeConfig: RuntimeConfig = await res.json()
                if (usesHostSignIn(runtimeConfig)) {
                    setConfig(runtimeConfig)
                    return
                }

                // Signed-in mode: the real client. Local development: an inert
                // one, only so useMsal() has a provider.
                const pca = new PublicClientApplication(createMsalConfig(runtimeConfig))
                // MSAL v3 refuses every call before initialize(); for the inert
                // client it sets up storage only, with no network round trip.
                await pca.initialize()
                if (runtimeConfig.auth_enabled) {
                    // Completes a sign-in redirect (consumes the #code hash). The
                    // result carries tokens — never log it.
                    await pca.handleRedirectPromise().catch((err: unknown) => {
                        console.error('Sign-in redirect failed')
                        setFailure({
                            title: 'Die Anmeldung ist fehlgeschlagen.',
                            message: 'Bitte neu laden und erneut anmelden.',
                            // MSAL's message (an AADSTS code and its reason) is English
                            // and for IT; the editor reads the German text above it.
                            detail: err instanceof Error ? err.message : String(err),
                        })
                        return null
                    })
                }
                setMsalInstance(pca)
                setConfig(runtimeConfig)
            } catch {
                setFailure({
                    title: 'Die Anwendung konnte nicht starten.',
                    message: 'Der Server ist nicht erreichbar. Bitte in einigen Sekunden neu laden.',
                })
            }
        }
        void init()
    }, [])

    if (isMsalPopup()) {
        return (
            <StartScreen>
                <StartProgress>Anmeldung wird abgeschlossen …</StartProgress>
            </StartScreen>
        )
    }

    if (failure) {
        return (
            <StartScreen>
                <StartError title={failure.title} detail={failure.detail}>
                    {failure.message}
                </StartError>
            </StartScreen>
        )
    }

    if (config && host && usesHostSignIn(config)) {
        return (
            <StrictMode>
                <ErrorBoundary>
                    <HostAuthProvider bridge={host}>
                        <App />
                    </HostAuthProvider>
                </ErrorBoundary>
            </StrictMode>
        )
    }

    if (!config || !msalInstance) {
        return (
            <StartScreen>
                <StartProgress>
                    {config ? 'Anmeldung wird vorbereitet …' : 'Verbindung zum Server wird hergestellt …'}
                </StartProgress>
            </StartScreen>
        )
    }

    return (
        <StrictMode>
            <ErrorBoundary>
                <MsalProvider instance={msalInstance}>
                    <AuthProvider authEnabled={config.auth_enabled}>
                        <App />
                    </AuthProvider>
                </MsalProvider>
            </ErrorBoundary>
        </StrictMode>
    )
}
